import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, UserCheck } from "lucide-react";
import { usePersons } from "@/hooks/usePersons";
import { ancestorLevels, descendantIds, years } from "@/lib/tree";
import { PersonCard, Avatar } from "@/components/PersonCard";
import { TreeView } from "@/components/FamilyBranch";
import { ZoomCanvas } from "@/components/ZoomCanvas";
import { PersonPhotos } from "@/components/PersonPhotos";
import { LineageActions } from "@/components/LineageActions";
import { PersonStories } from "@/components/PersonStories";
import { PersonDialog, NetWorthRow } from "@/components/PersonDialog";
import { PersonDocuments } from "@/components/PersonDocuments";
import { BranchTotal } from "@/components/BranchWealth";
import { ContributorBadge } from "@/components/ContributorBadge";
import { t as tr } from "@/lib/i18n";

const genLabel = (i) => (i === 0 ? tr("Parents") : i === 1 ? tr("Grandparents") : `${"Great-".repeat(i - 1)}grandparents`);

const Profile = ({ p, up, down }) => (
  <div className="flex flex-col sm:flex-row gap-6 items-start rise">
    <Avatar p={p} size="w-24 h-24 text-3xl" />
    <div className="flex-1">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43]">{tr("Lineage of")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mt-1" data-testid="lineage-person-name">{p.name}</h1>
      <p className="font-mono text-sm text-[#7E8E85] mt-2">{p.birth_date ? `${tr("Born")} ${new Date(p.birth_date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}${p.death_year ? ` · d. ${p.death_year}` : ""}` : years(p)}</p>
      {p.bio && <p className="mt-4 text-[#52635B] max-w-2xl leading-relaxed" data-testid="lineage-person-bio">{p.bio}</p>}
      <div className="mt-5 flex flex-wrap gap-3 text-sm">
        <span className="px-3 py-1.5 rounded-full bg-[#F4EDE2] flex items-center gap-1.5" data-testid="lineage-ancestor-count"><ArrowUp className="w-3.5 h-3.5" />{up} {tr("ancestors")}</span>
        <span className="px-3 py-1.5 rounded-full bg-[#F4EDE2] flex items-center gap-1.5" data-testid="lineage-descendant-count"><ArrowDown className="w-3.5 h-3.5" />{down} {tr("descendants")}</span>
        <ContributorBadge personId={p.person_id} />
        {p.has_account && <span className="px-3 py-1.5 rounded-full bg-[#E8F5E9] text-[#2E7D32] flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5" />{tr("Joined member")}</span>}
      </div>
    </div>
  </div>
);

export default function Lineage() {
  const { id } = useParams();
  const { persons, t } = usePersons();
  const p = t.byId[id];
  const levels = useMemo(() => (p ? ancestorLevels(id, t) : []), [p, id, t]);
  const desc = useMemo(() => (p ? descendantIds(id, t) : new Set()), [p, id, t]);
  const [sel, setSel] = useState(null);
  const open = (x) => setSel(x);

  if (persons === null) return <div className="max-w-7xl mx-auto p-8"><div className="h-64 rounded-3xl bg-[#F4EDE2] animate-pulse" /></div>;
  if (!p) return <div className="max-w-7xl mx-auto p-16 text-center font-serif text-2xl" data-testid="lineage-not-found">{tr("This person is not on the public tree.")}</div>;

  const ancestorCount = levels.reduce((n, l) => n + l.length, 0);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-10">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#52635B] hover:text-[#1E3A2B]" data-testid="lineage-back-link"><ArrowLeft className="w-4 h-4" />{tr("Full tree")}</Link>
      <Profile p={p} up={ancestorCount} down={desc.size} />
      <LineageActions p={p} />
      <div className="rounded-3xl border border-[#E5DEC9] bg-white px-6 max-w-2xl" data-testid="profile-networth-panel"><dl><NetWorthRow p={p} /></dl><BranchTotal p={p} tree={t} /></div>
      <ZoomCanvas testid="lineage-canvas" focusId={id}>
        <div className="flex flex-col items-center" data-testid="lineage-ancestors">
          {[...levels].reverse().map((level, ri) => {
            const i = levels.length - 1 - ri;
            return (
              <div key={i} className="flex flex-col items-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7E8E85] mb-2">{genLabel(i)}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {level.map((a) => <PersonCard key={a.person_id} p={a} tree={t} onClick={open} />)}
                </div>
                <div className="w-0.5 h-8 bg-[#CDBF9F] my-1 relative stem-bounce" />
              </div>
            );
          })}
        </div>
        <TreeView roots={[p]} t={t} onSelect={open} activeId={id} />
        {desc.size === 0 && <p className="text-center text-sm text-[#7E8E85] mt-6">{tr("No descendants recorded yet.")}</p>}
      </ZoomCanvas>
      {sel && <PersonDialog p={t.byId[sel.person_id] || sel} t={t} onClose={() => setSel(null)} onSelect={setSel} />}
      <PersonStories personId={id} name={p.name} />
      <PersonDocuments personId={id} />
      <PersonPhotos personId={id} name={p.name} />
    </div>
  );
}
